/**
 * Tiny, dependency-free Markdown → HTML renderer for the RadAI Assistant chat.
 *
 * Security: the input (LLM output) is HTML-escaped FIRST, then a limited, known
 * set of Markdown constructs is converted back to safe tags. Only http(s)/mailto
 * links are allowed. This avoids pulling in a markdown library while staying XSS-safe.
 */

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const safeHref = (raw: string): string | null => {
  const url = raw.trim();
  if (/^(https?:\/\/|mailto:)/i.test(url)) return url;
  return null;
};

/** Inline formatting: code, bold, italic, links. Operates on already-escaped text. */
const renderInline = (text: string): string => {
  // Inline code first so its contents are not further transformed.
  const codeStash: string[] = [];
  let out = text.replace(/`([^`]+)`/g, (_m, code) => {
    codeStash.push(`<code>${code}</code>`);
    return `\u0000${codeStash.length - 1}\u0000`;
  });

  // Links [text](url)
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, label: string, href: string) => {
      const h = safeHref(href);
      if (!h) return label;
      return `<a href="${h}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    }
  );

  // Bold then italic.
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/(^|[^_])_([^_\n]+)_/g, "$1<em>$2</em>");

  // Restore inline code.
  out = out.replace(/\u0000(\d+)\u0000/g, (_m, i) => codeStash[Number(i)] || "");
  return out;
};

export const renderMarkdown = (src: string): string => {
  if (!src) return "";
  const escaped = escapeHtml(src);
  const lines = escaped.split("\n");
  const html: string[] = [];

  let inCode = false;
  let codeBuf: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let paraBuf: string[] = [];

  const flushPara = () => {
    if (paraBuf.length) {
      html.push(`<p>${renderInline(paraBuf.join(" "))}</p>`);
      paraBuf = [];
    }
  };
  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine;

    // Fenced code blocks ```
    if (/^\s*```/.test(line)) {
      if (inCode) {
        html.push(`<pre><code>${codeBuf.join("\n")}</code></pre>`);
        codeBuf = [];
        inCode = false;
      } else {
        flushPara();
        closeList();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }

    // Blank line → paragraph / list break
    if (!line.trim()) {
      flushPara();
      closeList();
      continue;
    }

    // Headings
    const heading = line.match(/^\s*(#{1,6})\s+(.*)$/);
    if (heading) {
      flushPara();
      closeList();
      const level = Math.min(6, Math.max(3, heading[1].length + 2)); // map to h3..h6
      html.push(`<h${level}>${renderInline(heading[2].trim())}</h${level}>`);
      continue;
    }

    // Unordered list
    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    if (ul) {
      flushPara();
      if (listType !== "ul") {
        closeList();
        listType = "ul";
        html.push("<ul>");
      }
      html.push(`<li>${renderInline(ul[1].trim())}</li>`);
      continue;
    }

    // Ordered list
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ol) {
      flushPara();
      if (listType !== "ol") {
        closeList();
        listType = "ol";
        html.push("<ol>");
      }
      html.push(`<li>${renderInline(ol[1].trim())}</li>`);
      continue;
    }

    // Blockquote
    const bq = line.match(/^\s*>\s?(.*)$/);
    if (bq) {
      flushPara();
      closeList();
      html.push(`<blockquote>${renderInline(bq[1].trim())}</blockquote>`);
      continue;
    }

    // Plain paragraph text (accumulate)
    closeList();
    paraBuf.push(line.trim());
  }

  if (inCode) html.push(`<pre><code>${codeBuf.join("\n")}</code></pre>`);
  flushPara();
  closeList();

  return html.join("\n");
};
